import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, AlertTriangle, Check } from 'lucide-react';
import { API } from '../context/AdminAuthContext';

/**
 * Oct 2026 — Identify each order line by the backend's `order_line_id`,
 * NOT by `product_id`. Two lines on the same order can legitimately carry
 * the same product (e.g. a split price or a promo line). Keying React
 * state and the rendered cards on `product_id` made those two lines share
 * one entry: typing into one overwrote the other and one card vanished —
 * the exact "line disappeared" symptom. The endpoint already returns a
 * unique `order_line_id` ("L1", "L2", …) for every line; fall back to
 * product_id only if an older backend omits it.
 */
const _lineKey = (l) => l.order_line_id || l.product_id;

/**
 * ManualAllocateModal
 * -------------------
 * Lets the admin allocate every line on an order to specific
 * placements (or the unassigned batch pool) by picking a source +
 * qty per split. Supports multiple splits per line (e.g. 40 from
 * pallet A, 60 from pallet B). Prevents over-allocation client-side
 * so the Save button stays disabled until every line is either fully
 * or partially allocated within bounds. The backend re-validates.
 *
 * Props:
 *   order        — the order doc (needs `id`, `order_number`, `items`)
 *   authHeaders  — admin auth headers
 *   onClose      — close handler (no side effects)
 *   onDone       — called with the updated order after a successful save
 */
const ManualAllocateModal = ({ order, authHeaders, onClose, onDone }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [linesData, setLinesData] = useState([]);
  const [allocations, setAllocations] = useState({}); // { order_line_id: [{ source_idx, qty }] }
  const [error, setError] = useState('');

  // Load available sources for every line on this order.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/admin/orders/${order.id}/dispatch/available-sources`,
          { headers: authHeaders },
        );
        if (cancelled) return;
        setLinesData(r.data.lines || []);
        // Seed one empty row per line so the admin sees the form
        // structure immediately.
        //
        // Oct 2026 — Seed only lines we don't already hold rows for. Any
        // re-run of this loader must NEVER discard quantities the admin has
        // already typed; that was the "filled lines disappear" bug.
        setAllocations((prev) => {
          const next = { ...prev };
          for (const l of r.data.lines || []) {
            const k = _lineKey(l);
            if (!next[k]) next[k] = [{ source_idx: '', qty: '' }];
          }
          return next;
        });
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load available sources.');
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Oct 2026 — Deliberately keyed on order.id only. `authHeaders` is now
    // memoised in AdminAuthContext, but this loader must not re-fire on an
    // auth-object identity change under any circumstance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  // Compute per-line total + validity.
  const perLine = useMemo(() => {
    const out = {};
    for (const l of linesData) {
      const rows = allocations[_lineKey(l)] || [];
      let total = 0;
      let anyOver = false;
      const usedBySource = {};
      for (const row of rows) {
        const q = Number(row.qty) || 0;
        if (q > 0) {
          total += q;
          usedBySource[row.source_idx] = (usedBySource[row.source_idx] || 0) + q;
        }
      }
      // Over-source check: sum of qty on a given source > that
      // source's available_qty.
      for (const [srcIdx, used] of Object.entries(usedBySource)) {
        const src = l.sources[Number(srcIdx)];
        if (!src) continue;
        if (used > src.available_qty) anyOver = true;
      }
      const overOrdered = total > l.qty_ordered;
      out[_lineKey(l)] = {
        total,
        remaining: l.qty_ordered - total,
        overOrdered,
        overSource: anyOver,
        valid: !overOrdered && !anyOver,
      };
    }
    return out;
  }, [linesData, allocations]);

  const allValid = useMemo(() => {
    if (linesData.length === 0) return false;
    // At least one row must have a qty > 0, and no line is invalid.
    let anyAllocated = false;
    for (const l of linesData) {
      const s = perLine[_lineKey(l)];
      if (!s?.valid) return false;
      if (s.total > 0) anyAllocated = true;
    }
    return anyAllocated;
  }, [linesData, perLine]);

  const addRow = (pid) => {
    // Oct 2026 — Appends a new empty row and leaves scroll position
    // untouched. The previous scroll-into-view behaviour pushed
    // earlier filled rows above the viewport on each "Add split",
    // which users read as the earlier lines disappearing.
    setAllocations((prev) => ({
      ...prev,
      [pid]: [...(prev[pid] || []), { source_idx: '', qty: '' }],
    }));
  };
  const removeRow = (pid, idx) => {
    setAllocations((prev) => {
      const rows = [...(prev[pid] || [])];
      rows.splice(idx, 1);
      return { ...prev, [pid]: rows.length ? rows : [{ source_idx: '', qty: '' }] };
    });
  };
  const updateRow = (pid, idx, field, value) => {
    setAllocations((prev) => {
      const rows = [...(prev[pid] || [])];
      rows[idx] = { ...rows[idx], [field]: value };
      return { ...prev, [pid]: rows };
    });
  };

  const save = async () => {
    setSaving(true);
    setError('');
    // Build the payload the backend expects.
    const lines = linesData.map((l) => {
      const rows = allocations[_lineKey(l)] || [];
      const allocs = rows
        .filter((r) => r.source_idx !== '' && Number(r.qty) > 0)
        .map((r) => {
          const src = l.sources[Number(r.source_idx)];
          return {
            placement_id: src.placement_id,
            warehouse_id: src.warehouse_id,
            batch_ref: src.batch_ref,
            production_date: src.production_date,
            expiry_date: src.expiry_date,
            qty: Number(r.qty),
          };
        });
      return { product_id: l.product_id, allocations: allocs };
    }).filter((l) => l.allocations.length > 0);

    try {
      const r = await axios.post(
        `${API}/admin/orders/${order.id}/dispatch/manual-allocate`,
        { lines },
        { headers: authHeaders },
      );
      onDone?.(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const _sourceLabel = (s) => {
    const parts = [];
    parts.push(s.warehouse_name || 'Warehouse');
    if (s.unassigned) parts.push('(unassigned)');
    else if (s.zone || s.location_id || s.pallet_id) {
      const loc = [s.zone, s.location_id].filter(Boolean).join('-');
      const pallet = s.pallet_id ? `Pallet ${s.pallet_id}` : '';
      parts.push([loc, pallet].filter(Boolean).join(' · '));
    }
    if (s.batch_ref) parts.push(`Batch ${s.batch_ref}`);
    if (s.expiry_date) parts.push(`Exp ${s.expiry_date}`);
    return parts.join(' — ');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      // Oct 2026 — Backdrop clicks must NOT discard in-progress
      // allocation lines. Previously `onClick={onClose}` wiped
      // every filled split the moment a user tapped outside the
      // modal body (even by accident). The user must now exit via
      // the explicit Cancel / Close button, so a stray click is
      // harmless.
      data-testid="manual-allocate-modal-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="manual-allocate-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 shrink-0">
          <div>
            <h3 className="text-lg font-bold text-[#0F172A]">Manual Allocate</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              Order {order.order_number || order.id.slice(0, 8)} — pick where each line comes from
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700 p-1 rounded-full hover:bg-neutral-100 transition"
            data-testid="manual-allocate-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {loading && <div className="text-sm text-neutral-500 py-8 text-center">Loading available stock…</div>}
          {!loading && error && !linesData.length && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}
          {!loading && linesData.map((l) => {
            const k = _lineKey(l);
            const s = perLine[k] || {};
            const rows = allocations[k] || [];
            return (
              <div
                key={k}
                className="rounded-xl border border-neutral-200 p-4 bg-neutral-50/50"
                data-testid={`manual-allocate-line-${k}`}
              >
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div>
                    <div className="font-semibold text-[#0F172A] text-sm">{l.product_name}</div>
                    <div className="text-[11px] text-neutral-500">
                      Ordered: <span className="font-semibold tabular-nums">{l.qty_ordered}</span>
                      {l.already_allocated > 0 && (
                        <span className="ml-2">
                          Currently allocated: <span className="font-semibold tabular-nums">{l.already_allocated}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      s.overOrdered || s.overSource
                        ? 'bg-red-100 text-red-700'
                        : s.total > 0
                          ? s.total === l.qty_ordered
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                          : 'bg-neutral-200 text-neutral-500'
                    }`}
                    data-testid={`manual-allocate-summary-${k}`}
                  >
                    {s.total || 0} / {l.qty_ordered}
                    {s.overOrdered && ' · over'}
                    {s.overSource && !s.overOrdered && ' · source over'}
                  </div>
                </div>

                {l.sources.length === 0 && (
                  <div className="text-xs text-red-700 py-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> No warehouse stock available for this product.
                  </div>
                )}

                {l.sources.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {rows.map((row, idx) => {
                      const selectedSrc = row.source_idx !== '' ? l.sources[Number(row.source_idx)] : null;
                      const rowQty = Number(row.qty) || 0;
                      const rowOver = selectedSrc && rowQty > selectedSrc.available_qty;
                      return (
                        <div
                          key={idx}
                          className="flex items-start gap-2"
                          data-testid={`manual-allocate-row-${k}-${idx}`}
                        >
                          <select
                            value={row.source_idx}
                            onChange={(e) => updateRow(k, idx, 'source_idx', e.target.value)}
                            className="flex-1 min-w-0 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-xs focus:border-[#0078D4] focus:outline-none focus:ring-1 focus:ring-[#0078D4]"
                            data-testid={`manual-allocate-source-select-${k}-${idx}`}
                          >
                            <option value="">— Choose warehouse / location —</option>
                            {l.sources.map((src, sidx) => (
                              <option key={sidx} value={sidx}>
                                {_sourceLabel(src)} · avail {src.available_qty}
                              </option>
                            ))}
                          </select>
                          <input
                            type="number"
                            min="0"
                            max={selectedSrc?.available_qty || undefined}
                            value={row.qty}
                            onChange={(e) => updateRow(k, idx, 'qty', e.target.value)}
                            placeholder="Qty"
                            disabled={!selectedSrc}
                            className={`w-24 rounded-lg border px-3 py-2 text-xs tabular-nums text-right focus:outline-none focus:ring-1 ${
                              rowOver
                                ? 'border-red-300 bg-red-50 text-red-700 focus:border-red-400 focus:ring-red-400'
                                : 'border-neutral-300 bg-white focus:border-[#0078D4] focus:ring-[#0078D4]'
                            }`}
                            data-testid={`manual-allocate-qty-input-${k}-${idx}`}
                          />
                          {rows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRow(k, idx)}
                              className="text-neutral-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition"
                              data-testid={`manual-allocate-remove-row-${k}-${idx}`}
                              aria-label="Remove split"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {l.sources.length > 0 && (
                  <button
                    type="button"
                    onClick={() => addRow(k)}
                    className="text-[11px] font-semibold text-[#0078D4] hover:bg-[#DEECF9] px-2.5 py-1 rounded-full inline-flex items-center gap-1 mt-1"
                    data-testid={`manual-allocate-add-row-${k}`}
                  >
                    <Plus className="w-3 h-3" /> Add split
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-neutral-200 bg-neutral-50 shrink-0">
          {error && linesData.length > 0 && (
            <div className="text-xs text-red-700 flex items-center gap-1.5 flex-1">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {error}
            </div>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full text-sm font-semibold text-neutral-700 hover:bg-neutral-200 transition"
              data-testid="manual-allocate-cancel-btn"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!allValid || saving}
              className="px-4 py-2 rounded-full text-sm font-semibold text-white bg-[#0078D4] hover:bg-[#106EBE] disabled:bg-neutral-300 disabled:cursor-not-allowed transition inline-flex items-center gap-1.5"
              data-testid="manual-allocate-save-btn"
            >
              <Check className="w-4 h-4" />
              {saving ? 'Saving…' : 'Confirm allocation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualAllocateModal;
